import { MutableRefObject, SyntheticEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { VendorLocationFormField } from 'contacts/elements';
import { IContactGroupAddress } from 'contacts/types';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { mapVendorLocationToImportFormContact } from 'quotes/mappers';
import { IImportVendorQuoteForm } from 'quotes/types';

interface IProps {
  disabled?: boolean;
  /**
   * True only while the contact in the field is still the BE prefill. `core/components/Steps`
   * mounts the active step alone, so Back from Attributes remounts this field with the user's OWN
   * picks as defaults — adopting those into the ref below would let the next vendor change throw
   * away a hand-picked contact.
   */
  isContactPrefilled?: boolean;
  // A `no_extractor` import has no extractor id to key a mapping by, so nothing is saved for it and
  // the helper text must not promise the choice will be reused.
  isVendorMappingRemembered?: boolean;
  // The mapping stores a vendor that still resolves, and the BE finalize refuses to re-point it
  // (validateImportVendorSelection) — only the settings screen can change it.
  isVendorLocked?: boolean;
  // The extractor is `mapped`: the vendor is shown from a display-only form value, never posted.
  isVendorMapped?: boolean;
  // A re-import keeps the quote's own contact whatever is posted (contract §19.4), so a vendor pick
  // must not swap it for the site's primary contact.
  isContactLocked?: boolean;
  // Owned by a parent that outlives this field's remounts (Back remounts the wizard's contact
  // step), so the id of our own suggestion survives and is not mistaken for a manual pick.
  suggestedContactIdRef?: MutableRefObject<number | null>;
  name?: string;
}

// Vendor picker shown on the import prompt when the extractor has no usable mapping and the
// importer is on the alternate version. Picking a vendor pre-selects that vendor site's primary
// contact, which is only ever a suggestion — see the ref below.
export const ImportVendorLocationField = ({
  disabled = false,
  isContactPrefilled = false,
  isVendorMappingRemembered = false,
  isVendorLocked = false,
  isVendorMapped = false,
  isContactLocked = false,
  suggestedContactIdRef,
  name = 'vendorLocation',
}: IProps) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();

  // The contact id this component (or the BE prefill it started from) put in the field. A value
  // that no longer matches it was chosen by the user and must survive every later vendor change.
  const ownSuggestedContactIdRef = useRef<number | null>(null);
  const autoSelectedContactIdRef = suggestedContactIdRef ?? ownSuggestedContactIdRef;

  // Seeded once per mount, and only from the BE prefill: a remount with the user's own picks keeps
  // whatever the parent-owned ref already holds.
  const isSeededRef = useRef(false);

  if (!isSeededRef.current) {
    isSeededRef.current = true;

    if (isContactPrefilled) {
      autoSelectedContactIdRef.current =
        (getValues('contact') as IImportVendorQuoteForm['contact'])?.id ?? null;
    }
  }

  const isReadOnly = isVendorLocked || isVendorMapped;

  const helperText = isReadOnly
    ? t('messageVendorQuoteImportVendorMapped')
    : [
        t('messageVendorQuoteImportVendor'),
        isVendorMappingRemembered && t('messageVendorQuoteImportVendorRemembered'),
      ]
        .filter(Boolean)
        .join(' ');

  // The picked option carries the vendor site's resolved contact, so the pre-select is synchronous:
  // the pair on screen, which the finalize upserts onto the mapping, is never briefly mismatched.
  const onChangeVendorLocation = (_: SyntheticEvent, option: IAutocompleteOption) => {
    if (isContactLocked) {
      return;
    }

    const contact = getValues('contact') as IImportVendorQuoteForm['contact'];
    const isOurSuggestion = Boolean(contact) && contact.id === autoSelectedContactIdRef.current;

    // An explicit choice is never overwritten.
    if (contact && !isOurSuggestion) {
      return;
    }

    const prefilledContact = mapVendorLocationToImportFormContact(
      option as unknown as IContactGroupAddress
    );

    autoSelectedContactIdRef.current = prefilledContact?.id ?? null;

    if (prefilledContact) {
      setValue('contact', prefilledContact, { shouldValidate: true });

      return;
    }

    // The new vendor resolves nobody: drop what we suggested for the old one rather than leave it
    // next to a vendor it does not belong to. No `shouldValidate` — the contact is required, and
    // flagging it the instant a vendor is picked would read as the pick being wrong. The step's
    // Next still blocks on the empty field.
    if (isOurSuggestion) {
      setValue('contact', null);
    }
  };

  return (
    <VendorLocationFormField
      name={isVendorMapped ? 'mappedVendorLocation' : name}
      label={t('labelVendor')}
      placeholder={t('labelVendor')}
      required
      disabled={disabled || isReadOnly}
      helperTexts={[helperText]}
      onChange={onChangeVendorLocation}
    />
  );
};
